import { Component, Prop, Emit, Watch, PropSync } from 'vue-property-decorator';
import PageHeader from '@/components/page-header/PageHeader';
import { Season } from '@/models/Season';
import { Size } from '@/models/Size';
import { Category } from '@/models/Category';
import { Tag } from '@/models/Tag';
import { Attribute } from '@/models/Attribute';
import { AxiosError } from 'axios';
import { Color } from '@/models/Color';
import { Brand } from '@/models/Brand';
import { Supplier } from '@/models/Supplier';
import AbstractFilters from '@/components/CustomFilters/AbstractFilters';
import { UserFile } from '@/models/UserFile';
import debounce from "debounce";
import { Contact as ContactModel } from "@/models/Contact";
import ErrorHandler from '@/support/ErrorHandler';
import RenderFlow from '@/support/RenderFlow';
import { cloneDeep } from 'lodash';
import { Material } from '@/models/Material';
import { RoleEnum } from '@/models/User';
import SizeAutocomplete from '@/components/CustomAutocomplete/SizeAutocomplete/SizeAutocomplete.vue';

@Component<VariantFilters>({
  components: {
    PageHeader,
    SizeAutocomplete,
  },
})
export default class VariantFilters extends AbstractFilters {
  @Prop({ default: false }) protected hideCheckboxes!: boolean;

  @Prop({ default: false }) protected hasQuickFilters!: boolean;

  protected contact: ContactModel = new ContactModel();

  protected contactAnswers: any[] = [];

  protected contactImages: any[] = [];

  protected allSeasons: Season[] = [];

  protected sizes: Size[] = [];

  protected colors: FilterColor[] = [];

  protected categories: Category[] = [];

  protected tags: Tag[] = [];

  protected materials: Material[] = [];

  protected showOutofStock = false;

  protected showNoImages = false;

  protected showWithoutAttributes = false;

  protected showPeoplevoxDoNotSync = false;

  protected filtered = false;

  protected isLoadingFilters = true;

  protected optionFilter: OptionFilter = {};

  protected brands: Brand[] = [];

  protected suppliers: Supplier[] = [];

  protected attributes: Attribute[] = [];

  protected price: number[] = [0, 500];

  protected blockDates: string[] = ['', ''];

  protected finalReturnDates: string[] = ['', ''];

  protected RenderFlow = new RenderFlow([
    'isShowingBlockDateFromRangePicker',
    'isShowingBlockDateUntilRangePicker',
    'isShowingFinalReturnDateFromRangePicker',
    'isShowingFinalReturnDateUntilRangePicker',
  ]);

  protected brandSearch: string = "";
  protected supplierSearch: string = "";
  protected tagSearch: string = "";
  protected materialSearch: string = "";
  protected categorySearch: string = "";
  protected sizeSearch: string = "";

  protected quickFilters = [
    {
      label: 'Top Seller',
      value: 'top_seller',
      id: '9c40f821-4a15-4a9a-9a52-ce325fb4f850',
      active: false,
    },
    {
      label: 'AOV Booster',
      value: 'aov_booster',
      id: '9c40f838-ce80-4f61-8d5a-4fee1d0ca94d',
      active: false,
    },
    {
      label: 'Rising Star',
      value: 'rising_star',
      id: '9c40f82e-8c26-494c-8b2c-b00978801c1d',
      active: false,
    },
  ];

  public searchDebounce: Function = this.handleSearch();
  public searchBrandDebounce: Function = this.handleBrandSearch();
  public searchSupplierDebounce: Function = this.handleSupplierSearch();
  public searchCategoryDebounce: Function = this.handleCategorySearch();
  public searchTagDebounce: Function = this.handleTagSearch();
  public searchMaterialDebounce: Function = this.handleMaterialSearch();
  public searchSizeDebounce: Function = this.handleSizeSearch();
  public searchSeasonDebounce: Function = this.handleSeasonSearch();

  @PropSync('filters')
  protected productFilter!: ProductVariantFilterItem;

  @Prop()
  protected hideExtra!: boolean; // The product overview has all filters, the webshop does not

  @PropSync('defaultFilters')
  protected defaultFilter!: ProductVariantFilterItem;

  public async mounted() {
    await this.initialize();

    this.showNoImages = this.productFilter.no_images === '1';
    this.showOutofStock = this.productFilter.out_of_stock === '1';
    this.showWithoutAttributes = this.productFilter.withoutAttributes === '1';
    this.showPeoplevoxDoNotSync = this.productFilter.peoplevox_do_not_sync === '1';

    const productFilter = this.getProductFilters();

    if (productFilter) {
      this.productFilter = {
        ...productFilter,
        status: typeof productFilter.status === 'string' ? [productFilter.status] : productFilter.status,
        seasons: Array.isArray(productFilter.seasons) ? productFilter.seasons : (productFilter.seasons || (productFilter as any).season ? [productFilter.seasons || (productFilter as any).season] : [])
      };
    } else {
      this.productFilter = productFilter;
    }

    this.filtered = this.isFiltered;
    this.isLoadingFilters = false;
    this.filtersLoaded();
  }

  protected async initialize() {
    await Promise.all([
      this.getBrands(),
      this.getColors(),
      this.getSizes(),
      this.getSuppliers(),
      this.getCategories(),
      this.getSeasons(),
      this.getTags(),
      this.getMaterials(),
      this.getAttributes(),
      this.formatFilterDates(),
    ]);
  }

  @Emit()
  protected refreshDataTable() {
    return this.productFilter;
  }

  @Emit()
  protected filtersLoaded() {
    return '';
  }

  @Emit()
  protected filterUpdated() {
    return this.productFilter;
  }

  protected async formatFilterDates() {
    const blockDates = [
      this.productFilter.block_date_from ? `${this.formatDateToISO(this.productFilter.block_date_from)}` : '',
      this.productFilter.block_date_until ? `${this.formatDateToISO(this.productFilter.block_date_until)}` : ''
    ];

    this.$set(this, 'blockDates', blockDates);

    const finalReturnDates = [
      this.productFilter.final_return_date_from ? `${this.formatDateToISO(this.productFilter.final_return_date_from)}` : '',
      this.productFilter.final_return_date_until ? `${this.formatDateToISO(this.productFilter.final_return_date_until)}` : ''
    ];

    this.$set(this, 'finalReturnDates', finalReturnDates);
  }

  protected removeSizeAndColor(index: number) {
    (this.productFilter.options as any).splice(index, 1);
  }

  protected removeSize(index: number) {
    (this.productFilter.sizes as any).splice(index, 1);
  }

  protected removeColor(index: number) {
    (this.productFilter.colors as any).splice(index, 1);
  }

  protected removeBrand(index: number) {
    (this.productFilter.brands as any).splice(index, 1);
  }

  protected priceRangeChange() {
    this.productFilter["min_price"] = this.price[0].toString();
    this.productFilter["max_price"] = this.price[1].toString();
    this.filterChange();
  }

  protected blockDateRangeChange() {
    this.productFilter['block_date_from'] = this.formatDate(this.blockDates[0]) as string;
    this.productFilter['block_date_until'] = this.formatDate(this.blockDates[1]) as string;
    this.filterChange();
  }

  protected finalReturnDateRangeChange() {
    this.productFilter['final_return_date_from'] = this.formatDate(this.finalReturnDates[0]) as string;
    this.productFilter['final_return_date_until'] = this.formatDate(this.finalReturnDates[1]) as string;
    this.filterChange();
  }

  protected clearPriceRange() {
    this.$set(this, "price", [0, 500]);
    delete this.productFilter.min_price;
    delete this.productFilter.max_price;
    this.filterChange();
  }

  protected clearBlockDateRange() {
    this.$set(this, 'blockDates', ['', '']);
    this.productFilter.block_date_from = '';
    this.productFilter.block_date_until = '';
    this.filterChange();
  }

  protected clearFinalReturnDateRange() {
    this.$set(this, 'finalReturnDates', ['', '']);
    this.productFilter.final_return_date_from = '';
    this.productFilter.final_return_date_until = '';
    this.filterChange();
  }

  protected onToggleQuickFilter(quickFilter: Record<string, any>): void {
    if (this.productFilter.tags === undefined) {
      this.productFilter.tags = [];
    }

    quickFilter.active = !quickFilter.active;

    if (quickFilter.active) {
      if (!this.productFilter.tags) { return; }
      this.productFilter.tags.push(quickFilter.id);
    } else {
      if (!this.productFilter.tags) { return; }
      this.productFilter.tags = this.productFilter.tags.filter((tag: string) => tag !== quickFilter.id);
    }

    this.filterChange();
  }

  protected get formatFromBlockDate() {
    return this.formatDate(this.blockDates[0]);
  }

  protected get formatUntilBlockDate() {
    return this.formatDate(this.blockDates[1]);
  }

  protected get formatFinalReturnDateFrom() {
    return this.formatDate(this.finalReturnDates[0]);
  }

  protected get formatFinalReturnDateUntil() {
    return this.formatDate(this.finalReturnDates[1]);
  }

  protected formatDate(date: string) {
    if (!date) return null

    const [year, month, day] = date.split('-')
    return `${day}-${month}-${year}`
  }

  protected formatDateToISO(date: string) {
    if (!date) return null

    const [day, month, year] = date.split('-')
    return `${year}-${month}-${day}`
  }

  protected filterChange() {
    this.setProductFilters(this.productFilter);
    this.filterUpdated();

    this.filtered = this.isFiltered;
  }

  protected resetFilter(type: string) {
    this.$set(this.productFilter, type, []);
  }

  protected resetAllFilters() {
    this.$storage.clear();
    this.showNoImages = false;
    this.showOutofStock = false;
    this.showWithoutAttributes = false;
    this.showPeoplevoxDoNotSync = false;
    this.$set(this, 'blockDates', ['', '']);
    this.productFilter.block_date_from = '';
    this.productFilter.block_date_until = '';
    this.$set(this, 'finalReturnDates', ['', '']);
    this.productFilter.final_return_date_from = '';
    this.productFilter.final_return_date_until = '';
    this.$set(this, "price", [0, 500]);
    delete this.productFilter.min_price;
    delete this.productFilter.max_price;
    this.$set(this.productFilter, "attributes", []);
    this.$set(this.productFilter, "colors", []);
    this.$set(this.productFilter, "categories", []);
    this.$set(this.productFilter, "suppliers", []);
    this.$set(this.productFilter, "tags", []);
    this.$set(this.productFilter, "materials", []);
    this.$set(this.productFilter, "sizes", []);
    this.$set(this.productFilter, "brands", []);
    this.$set(this.productFilter, "seasons", []);
    // In the webshop, the default should be active. If you are a photo_studio user you can never select inactive, so we just reset it back active
    this.$set(this.productFilter, "status", this.isPageWebshopOrder || this.isUserRolePhotoStudioOrAdmin ? ['active'] : []);
    this.$set(this.productFilter, "is_rerun", undefined);
    this.$set(this.productFilter, "is_dummy_upc", undefined);
    this.productFilter = cloneDeep(this.defaultFilter);
    this.$set(this.productFilter, "tags", []);
    this.quickFilters.forEach((quickFilter: Record<string, any>) => {
      quickFilter.active = false;
    })

    this.filtered = false;
    this.setProductFilters({} as ProductVariantFilterItem);
    this.filterUpdated();
  }

  protected get isFiltered(): boolean {
    const filter = this.productFilter;
    if (
      filter.all_variants !== "1" ||
      (filter.categories && filter.categories.length) ||
      (filter.colors && filter.colors.length) ||
      (filter.sizes && filter.sizes.length) ||
      (filter.brands && filter.brands.length) ||
      (filter.suppliers && filter.suppliers.length) ||
      (filter.tags && filter.tags.length) ||
      filter.min_price ||
      filter.max_price ||
      filter.block_date_from ||
      filter.block_date_until ||
      filter.final_return_date_from ||
      filter.final_return_date_until ||
      filter.no_images !== "0" ||
      filter.out_of_stock !== "0" ||
      filter.withoutAttributes !== "0" ||
      filter.peoplevox_do_not_sync !== "0" ||
      (this.isStatusFilterAvailable && filter.status && filter.status.length)
    ) {
      return true;
    }

    return false;
  }

  protected orderFilters(key: string) {
    if (!(this.productFilter as any)[key]) {
      return;
    }

    const productFilter: string[] = (this.productFilter as any)[key];
    const filter = (this as any)[key];

    let checkedFilterOptions: any[] = filter.filter((filterOption: any) => {
      if (this.isOptionSelected(productFilter, filterOption.id)) {
        return true;
      }

      if (this.hasChildSelected(productFilter, filterOption)) {
        return true;
      }

      return false;
    });


    let uncheckedFilterOptions: any[] = filter.filter((filterOption: any) => {
      if (this.isOptionSelected(productFilter, filterOption.id)) {
        return false;
      }

      if (this.hasChildSelected(productFilter, filterOption)) {
        return false;
      }

      return true;
    });


    (this as any)[key] = [...checkedFilterOptions, ...uncheckedFilterOptions];

  }

  private isOptionSelected(selectedOptions: string[], currentOptionId: string): boolean {
    return selectedOptions.includes(currentOptionId);
  }

  private hasChildSelected(selectedOptions: string[], currentOption: any): boolean {
    const children = currentOption.children;
    if (!children || !children.length) {
      return false;
    }

    const childIds = children.map((child: any) => {
      return child.id;
    });

    return childIds.some((id: string) => {
      return selectedOptions.includes(id)
    });
  }

  protected async getBrands(filter: string = "") {
    this.brands = await this.$storage.remember<any[]>(`product_variants_filter_brands_${filter}`, async () => {
      await new Brand()
        .filter("search", filter)
        .limit(100)
        .sort("name", "ASC")
        .all()
        .then((brands: Brand[]) => {
          this.brands = brands;
        })
        .catch((error: AxiosError) => { });

      return this.brands;
    })
  }


  protected async getSuppliers(filter: string = "") {
    this.suppliers = await this.$storage.remember<any[]>(`product_variants_filter_suppliers_${filter}`, async () => {
      await new Supplier()
        .filter("search", filter)
        .limit(100)
        .sort("name", "ASC")
        .all()
        .then((suppliers: Supplier[]) => {
          this.suppliers = suppliers;
        })
        .catch((error: AxiosError) => {
        });
      return this.suppliers;
    });
  }

  protected async getCategories(filter: string = "") {
    this.categories = await this.$storage.remember<any[]>(`product_variants_filter_categories_${filter}`, async () => {
      await new Category()
        .filter({ only_main: true })
        .limit(100)
        .sort("name", "ASC")
        .all()
        .then((categories: Category[]) => {
          this.categories = categories;

          this.categories.forEach((category: any) => {
            (category as any)["children"] = category.sub_categories;
          });
        })
        .catch((error: AxiosError) => {
        });
      return this.categories;
    });
  }

  protected async getTags(filter: string = "") {
    this.tags = await this.$storage.remember<any[]>(`product_variants_filter_tags_${filter}`, async () => {
      await new Tag()
        .filter("search", filter)
        .limit(100)
        .sort("name", "ASC")
        .all()
        .then((tags: Tag[]) => {
          this.tags = tags;
        })
        .catch((error: AxiosError) => {
        });
      return this.tags;
    });
  }

  protected async getMaterials(filter: string = "") {
    this.materials = await this.$storage.remember<any[]>(`product_variants_filter_materials_${filter}`, async () => {
      await new Material()
        .filter("search", filter)
        .limit(100)
        .sort("name", "ASC")
        .all()
        .then((materials: Material[]) => {
          this.materials = materials;
        })
        .catch((error: AxiosError) => {
        });
      return this.materials;
    });
  }

  protected async getSizes(filter: string = "") {
    this.sizes = await this.$storage.remember<any[]>(`product_variants_filter_sizes_${filter}`, async () => {
      await new Size()
        .filter("search", filter)
        .limit(100)
        .sort("name", "ASC")
        .all()
        .then((sizes: Size[]) => {
          this.sizes = sizes;
        })
        .catch((error: AxiosError) => {
        });

      return this.sizes;
    });
  }

  protected async getSeasons(filter: string = "") {
    this.allSeasons = await this.$storage.remember<any[]>(`product_variants_filter_seasons_${filter}`, async () => {
      await new Season()
        .filter("search", filter)
        .limit(100)
        .sort("name", "ASC")
        .all()
        .then((seasons: Season[]) => {
          this.allSeasons = seasons;
        })
        .catch((error: AxiosError) => {
        });
      return this.allSeasons;
    });
  }

  protected async getColors() {
    this.colors = await this.$storage.remember<any[]>('product_variants_filter_colors', async () => {
      await new Color()
        .limit(100)
        .sort("name", "ASC")
        .filter({ only_main: true })
        .all()
        .then((colors: Color[]) => {
          this.colors = colors;

          this.colors.forEach((color: any) => {
            (color as any)["children"] = color.sub_colors;
          });
        })
        .catch((error: AxiosError) => {
        });

      return this.colors;
    });
  }

  protected async getAttributes() {
    this.attributes = await this.$storage.remember<any[]>(`product_variants_filter_attributes`, async () => {
      await new Attribute().include('options')
        .filter('type', 'select')
        .limit(100)
        .all()
        .then((attributes: Attribute[]) => {
          this.attributes = attributes
        })
        .catch((error: AxiosError) => {
        });
      return this.attributes;
    });
  }

  protected handleAttributeChange(attribute: string, options: string[]) {
    if (!this.productFilter || !this.productFilter.attributes) {
      return;
    }

    // Remove existing entries for this attribute
    const otherAttributes = this.productFilter.attributes.filter(
      (a: attributeFilter) => a.attribute !== attribute,
    );

    // Add new entries for each selected option
    const newEntries = (options || []).map((option: string) => ({ attribute, option }));

    this.$set(this.productFilter, 'attributes', [...otherAttributes, ...newEntries]);
  }

  protected handleAttributeClear(attribute: string) {
    if (!this.productFilter || !this.productFilter.attributes) {
      return;
    }

    const clearedAttributes = this.productFilter.attributes.filter(
      (a: attributeFilter) => a.attribute !== attribute,
    );

    this.$set(this.productFilter, 'attributes', clearedAttributes);
  }

  protected isAttributeFiltered(attribute: string) {
    if (!this.productFilter || !this.productFilter.attributes) {
      return false;
    }

    return this.productFilter.attributes.some(indexedAttribute => indexedAttribute.attribute === attribute);
  }

  protected getAttributeValue(attribute: string) {
    if (!this.productFilter || !this.productFilter.attributes) {
      return [];
    }

    return this.productFilter.attributes
      .filter((a: attributeFilter) => a.attribute === attribute)
      .map((a: attributeFilter) => a.option);

    return '';
  }


  protected fetchFiles() {
    new UserFile()
      .user(this.contact)
      .limit(100)
      .all()
      .then((images: any) => {
        this.contactImages = images;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected outOfStock() {
    if (this.showOutofStock) {
      this.productFilter["out_of_stock"] = "1";
    } else {
      this.productFilter["out_of_stock"] = "0";
    }
    this.filterChange();
  }

  protected noImages() {
    if (this.showNoImages) {
      this.productFilter["no_images"] = "1";
    } else {
      this.productFilter["no_images"] = "0";
    }
    this.filterChange();
  }

  protected withoutAttributes() {
    if (this.showWithoutAttributes) {
      this.productFilter['withoutAttributes'] = '1';
    } else {
      this.productFilter['withoutAttributes'] = '0';
    }
    this.filterChange();
  }

  protected peoplevoxDoNotSync() {
    if (this.showPeoplevoxDoNotSync) {
      this.productFilter['peoplevox_do_not_sync'] = '1';
    } else {
      this.productFilter['peoplevox_do_not_sync'] = '0';
    }
    this.filterChange();
  }

  protected get rerunOptions() {
    return [
      {
        title: 'Is Rerun',
        value: '1',
      },
      {
        title: 'Is not Rerun',
        value: '0',
      },
    ]
  }

  protected get dummyUpcOptions() {
    return [
      {
        title: 'Is Dummy UPC',
        value: '1',
      },
      {
        title: 'Is not Dummy UPC',
        value: '0',
      },
    ]
  }

  protected get isNoImagesFilterAvailable(): boolean {
    return !this.isPageWebshopOrder
      || this.isUserRolePhotoStudioOrAdmin;
  }

  protected get isPageWebshopOrder(): boolean {
    return this.$route.name === 'webshop.order';
  }

  protected get isStatusFilterAvailable(): boolean {
    return !this.isPageWebshopOrder
      || this.isUserRolePhotoStudioOrAdmin;
  }

  protected get isUserRolePhotoStudioOrAdmin(): boolean {
    return this.$store.state.Auth
      && this.$store.state.Auth.hasRole(RoleEnum.PHOTO_STUDIO, true);
  }

  protected get isUserRoleAdmin(): boolean {
    return this.$store.state.Auth
      && this.$store.state.Auth.hasRole([], true);
  }

  protected get isWithoutAttributesFilterAvailable(): boolean {
    return !this.isPageWebshopOrder
      || this.isUserRolePhotoStudioOrAdmin;
  }

  protected get isPeoplevoxDoNotSyncFilterAvailable(): boolean {
    return !this.isPageWebshopOrder
      || this.isUserRolePhotoStudioOrAdmin;
  }

  protected get status(): string[] {
    if (!this.isStatusFilterAvailable) { return []; }

    return [
      "concept",
      "active",
      ...!this.isPageWebshopOrder || this.isUserRoleAdmin
        ? ["inactive"]
        : [],
    ];
  }

  public handleSearch() {
    return debounce((searchInput: string) => {
      this.productFilter["search"] = searchInput;
      this.refreshDataTable();
    }, 400);
  }

  protected handleBrandSearch() {
    return debounce((searchInput: string) => {
      this.getBrands(searchInput);
    }, 400);
  }

  protected handleSupplierSearch() {
    return debounce((searchInput: string) => {
      this.getSuppliers(searchInput);
    }, 400);
  }

  protected handleTagSearch() {
    return debounce((searchInput: string) => {
      this.getTags(searchInput);
    }, 400);
  }

  protected handleMaterialSearch() {
    return debounce((searchInput: string) => {
      this.getMaterials(searchInput);
    }, 400);
  }

  protected handleCategorySearch() {
    return debounce((searchInput: string) => {
      this.getCategories(searchInput);
    }, 400);
  }

  protected handleSizeSearch() {
    return debounce((searchInput: string) => {
      this.getSizes(searchInput);
    }, 400);
  }

  protected handleSeasonSearch() {
    return debounce((searchInput: string) => {
      this.getSeasons(searchInput);
    }, 400);
  }

  @Watch("brandSearch")
  protected brandSearchChanged(brand: string) {
    this.searchBrandDebounce(brand);
  }

  @Watch("supplierSearch")
  protected supplierSearchChanged(brand: string) {
    this.searchSupplierDebounce(brand);
  }

  @Watch("categorySearch")
  protected categorySearchChanged(brand: string) {
    this.searchCategoryDebounce(brand);
  }

  @Watch("tagSearch")
  protected tagSearchChanged(brand: string) {
    this.searchTagDebounce(brand);
  }

  @Watch("sizeSearch")
  protected sizeSearchChanged(size: string) {
    this.searchSizeDebounce(size);
  }
}


export interface ProductVariantFilterItem {
  categories?: string[];
  suppliers?: string[];
  tags?: string[];
  materials?: string[];
  sizes?: string[];
  colors?: string[];
  brands?: string[];
  withoutAttributes?: string;
  peoplevox_do_not_sync?: string
  attributes?: attributeFilter[];
  min_price?: string;
  max_price?: string;
  productFilter?: string;
  out_of_stock?: string;
  no_images?: string;
  search?: string;
  options?: object[];
  product?: string;
  all_variants?: string;
  status?: string[];
  seasons?: string[];
  block_date_from: string;
  block_date_until: string;
  final_return_date_from: string;
  final_return_date_until: string;
  order?: string;
}

interface OptionFilter {
  Size?: string;
  Brand?: string;
  Colour?: string;
}

interface FilterColor {
  id?: string;
  name?: string;
  type?: string;
  reference?: string;
}

interface attributeFilter {
  attribute?: string;
  option?: string;
}
